/**
 * The word arithmetic that decides whether a sync will succeed.
 *
 * Usage is billed as `source_word_count × number_of_target_locales`
 * (localingos-translation-worker/src/billing/word-counter.ts). That multiplier is
 * the single most surprising thing about the product's pricing, and until now it
 * was disclosed only in /docs — while /pricing advertised "5,000 words" and "56
 * locales" beside each other, which reads as though the two are independent.
 *
 * They are not, and the consequence is severe. A first sync is the most expensive
 * event in a project's life: every string is new, so every string is billed against
 * every locale. Afterwards only changed strings are re-translated, so steady-state
 * usage is a small fraction of it. A visitor who selects all 56 locales on the free
 * tier has 89 source words to spend — perhaps thirty short UI strings — and the
 * sync they were invited to run cannot complete.
 *
 * These helpers exist so the estimate shown on /pricing, the estimate shown while
 * picking locales, and the CLI's pre-flight check all agree, and so the numbers are
 * derived rather than written into copy by hand.
 */

/** Word allowance of each plan, as PlanConfig.java defines it. */
export const PLAN_ALLOWANCES = [
  { key: 'free', name: 'Free', words: 5_000, annualPriceUsd: 0 },
  { key: 'starter', name: 'Starter', words: 50_000, annualPriceUsd: 108 },
  { key: 'growth', name: 'Growth', words: 250_000, annualPriceUsd: 348 },
  { key: 'scale', name: 'Scale', words: 1_000_000, annualPriceUsd: 948 },
] as const;

export type PlanKey = typeof PLAN_ALLOWANCES[number]['key'];
export type Plan = typeof PLAN_ALLOWANCES[number];

/** Total locales the product supports, matching LocaleCode. */
export const SUPPORTED_LOCALE_COUNT = 56;

/**
 * Words billed for translating `sourceWords` into `localeCount` locales.
 *
 * Mirrors calculateBillableWords in the translation worker. Kept as its own
 * function so the multiplication is named rather than inlined at three call sites.
 */
export function billableWords(sourceWords: number, localeCount: number): number {
  if (sourceWords <= 0 || localeCount <= 0) return 0;
  return Math.round(sourceWords) * Math.round(localeCount);
}

/**
 * How many source words an allowance stretches to across `localeCount` locales.
 *
 * This is the number that makes the multiplier concrete: 5,000 words is a
 * comfortable budget for three locales and almost nothing for fifty-six.
 */
export function sourceWordsWithin(allowanceWords: number, localeCount: number): number {
  if (localeCount <= 0 || allowanceWords <= 0) return 0;
  return Math.floor(allowanceWords / localeCount);
}

/**
 * The cheapest plan whose allowance covers this sync, or null if none does.
 *
 * Null is a real answer rather than an error: 20,000 source words into all 56
 * locales is 1.12M words, past the largest plan, and saying so is more useful than
 * recommending Scale and letting the sync fail.
 */
export function smallestPlanFor(sourceWords: number, localeCount: number): Plan | null {
  const needed = billableWords(sourceWords, localeCount);
  if (needed === 0) return null;
  return PLAN_ALLOWANCES.find(plan => plan.words >= needed) ?? null;
}

/** Whether a sync of this size fits in what remains of an allowance. */
export function fitsInRemaining(
  sourceWords: number,
  localeCount: number,
  remainingWords: number
): boolean {
  return billableWords(sourceWords, localeCount) <= remainingWords;
}
